import React, { useContext, useEffect } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { faSyncAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';

import { RootState } from '../../app/rootReducer';
import { AppDispatch } from '../../app/store';
import { Resource } from '../../common/constants/resource';
import { UserOrganization } from '../../common/constants/userOrganization';
import { clientEnvVars } from '../../common/environment/clientEnvVars';
import { getFormattedTimestamp } from '../../common/helpers';
import { ProductFactory } from '../../common/productConfig/productFactory';
import AppHeader from '../layout/header/AppHeader';
import { SnackbarContext } from '../layout/SnackbarContext';
import { TaskTypes } from '../tasks/types/taskTypes';
import {
  erroredTaskListActions,
  fetchErroredTasks,
} from './erroredTaskListSlice';
import { retryEvidence } from './erroredTaskListThunks';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const TaskErrorsDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openSnacks, setSnacks } = useContext(SnackbarContext);

  const { reload, loading, error, items, pagination, filters } = useSelector(
    state => state.erroredTaskList,
  );

  const handlePageChange = (_: any, page: number) => {
    if (!loading) {
      dispatch(erroredTaskListActions.onPageUpdate({ page }));
    }
  };

  const handleTaskTypeFilterChange = (event: SelectChangeEvent<string>) => {
    dispatch(
      erroredTaskListActions.changeTaskTypeFilter({
        taskType: event.target.value as string,
      }),
    );
  };

  const handleProductCarrierFilterChange = (
    event: SelectChangeEvent<string>,
  ) => {
    const [product, carrier] = event.target.value.split(' ');
    dispatch(
      erroredTaskListActions.changeProductCarrierFilter({
        product,
        carrier,
      }),
    );
  };

  const handleTaskRetry = (applicationId: number, taskId: number) => {
    dispatch(retryEvidence(applicationId, taskId, openSnacks, setSnacks));
  };

  useEffect(() => {
    if (reload) {
      dispatch(
        fetchErroredTasks(pagination.page, pagination.pageSize, filters),
      );
    }
  }, [reload, pagination, filters, dispatch]);

  return (
    <Box>
      <AppHeader />

      <Box m={0.5} pt={2} pr={1} overflow="hidden">
        <StyledPaper elevation={0}>
          <TopHeader>
            <Typography variant="h1">Errored tasks</Typography>
            <Box display="flex" flexDirection="row">
              <Box width="20ch">
                <FormControl fullWidth>
                  <InputLabel id="task-type-select-label">
                    <Typography variant="body2">Evidence type</Typography>
                  </InputLabel>
                  <Select
                    labelId="task-type-select-label"
                    variant="outlined"
                    value={filters.taskType || ''}
                    onChange={handleTaskTypeFilterChange}
                  >
                    <MenuItem value="">
                      <Typography variant="body2">(Clear selection)</Typography>
                    </MenuItem>
                    {Object.values(TaskTypes).map(taskType => (
                      <MenuItem value={taskType} key={taskType}>
                        <Typography variant="body2">{taskType}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box width="20ch" mx={1}>
                <FormControl fullWidth>
                  <InputLabel id="product-carrier-select-label">
                    <Typography variant="body2">Product/Carrier</Typography>
                  </InputLabel>
                  <Select
                    labelId="product-carrier-select-label"
                    variant="outlined"
                    value={
                      filters.product && filters.carrier
                        ? `${filters.product} ${filters.carrier}`
                        : ''
                    }
                    onChange={handleProductCarrierFilterChange}
                  >
                    <MenuItem value="">
                      <Typography variant="body2">(Clear selection)</Typography>
                    </MenuItem>
                    {ProductFactory.getProductsByOrganization(
                      UserOrganization.ETHOS,
                    )
                      .filter(product => product.showInErrorTasks())
                      .map(product => (
                        <MenuItem
                          value={`${product.getProduct()} ${product.getCarrier()}`}
                          key={`${product.getProduct()} ${product.getCarrier()}`}
                        >
                          <Typography variant="body2">{`${product.getProduct()} ${product.getCarrier()}`}</Typography>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </TopHeader>

          {!error && items.length === 0 && (
            <ErrorContainer>
              <Typography color="textSecondary">
                No data matching your criteria
              </Typography>
            </ErrorContainer>
          )}

          {!error && items.length > 0 && (
            <TableContainer style={{ height: '100%' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      'Policy number',
                      'Product / Carrier',
                      'Evidence type',
                      'Error',
                      'Approx. error time',
                      'Underwriter',
                      'Case manager',
                      'UWX',
                      '',
                    ].map(header => (
                      <TableCell key={header}>
                        <Typography>{header}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Link
                          variant="body2"
                          target="_blank"
                          href={`/${clientEnvVars().NORA_SUBDIR}/applications/${
                            row.applicationId
                          }`}
                        >
                          {row.carrierPolicyNumber}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.product} {row.carrier}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.taskType}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.errorMessage ?? Resource.NORA_M_DASH}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getFormattedTimestamp(row.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.underwriter ?? Resource.NORA_M_DASH}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.casemanager ?? Resource.NORA_M_DASH}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.uwx ?? Resource.NORA_M_DASH}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.retrying ? (
                          <Box mt={0.5} ml={0.5}>
                            <CircularProgress size="0.75em" color="inherit" />
                          </Box>
                        ) : !row.retried ? (
                          <Tooltip title="Retry">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleTaskRetry(row.applicationId, row.taskId)
                              }
                            >
                              <FontAwesomeIcon
                                style={{ width: '0.75em' }}
                                icon={faSyncAlt}
                              />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box mt={2}>
            <TablePagination
              rowsPerPageOptions={[10]}
              count={pagination.count}
              rowsPerPage={pagination.pageSize}
              page={pagination.page}
              onPageChange={handlePageChange}
              component="div"
            />
          </Box>
        </StyledPaper>
      </Box>
    </Box>
  );
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  margin: theme.spacing(0, 1),
}));

const TopHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #e0e0e0',
  margin: theme.spacing(1),
  paddingBottom: theme.spacing(4),
}));

const ErrorContainer = styled(Box)(({}) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '50vh',
  justifyContent: 'center',
  alignItems: 'center',
}));
